/**
 * Output Footer Sections designer (super-admin)
 *
 * Rich-text sections appended to the estimate results after the last tables
 * and before the disclaimer — on screen (always / collapsible / hidden), in
 * print and in the PDF, with per-section page controls. The Universal set is
 * what every retailer gets; a retailer scope shows the EFFECTIVE list, where
 * each universal section can be customised (a retailer copy that replaces it)
 * or hidden (a marker row that removes it), and retailer-only sections can be
 * added. Content is semantic HTML (the server strips styling) so the results
 * page, print stylesheet and PDF apply the theme / retailer branding.
 */

import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  AlertTriangle, ArrowDown, ArrowUp, Copy, Eye, EyeOff, FileText, Loader2, Plus, RotateCcw, Save, Trash2, X,
} from 'lucide-react';
import {
  type AdminOutputSection, type OutputSectionRetailerSummary, type OutputSectionPayload,
  fetchOutputSections, createOutputSection, updateOutputSection, reorderOutputSections, deleteOutputSection,
} from '../../services/adminOutputSectionsApi';
import { saveTranslations, type TranslationValues } from '../../services/translationsApi';
import { TranslationFields } from '../../components/admin/TranslationFields';
import {
  type OutputSectionContent, type SectionScreenMode, SCREEN_MODES, DEFAULT_DISPLAY, DEFAULT_PAGE, SECTION_MAX_PAGES,
} from '../../config/outputSections';
import '../../components/results/OutputSections.css';

const RichTextEditor = lazy(() => import('../../components/admin/RichTextEditor'));

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

interface Draft {
  id: number | null;
  retailerId: number | null;
  key: string;
  title: string;
  content: OutputSectionContent;
  isActive: boolean;
}

/** One line of the effective list for the selected scope. */
interface Row {
  section: AdminOutputSection;
  /** Universal row shown inside a retailer scope (not editable there). */
  inherited: boolean;
  /** The retailer's hide marker for this universal key, when one exists. */
  hiddenBy: AdminOutputSection | null;
}

const blankDraft = (retailerId: number | null): Draft => ({
  id: null,
  retailerId,
  key: `s${Date.now().toString(36)}`,
  title: 'New section',
  content: { html: '<p></p>', display: { ...DEFAULT_DISPLAY }, page: { ...DEFAULT_PAGE } },
  isActive: true,
});

const byOrder = (a: AdminOutputSection, b: AdminOutputSection) => a.sortOrder - b.sortOrder || a.id - b.id;

/** The list the results page would show for a scope, plus admin-only rows (inactive, hide markers). */
function effectiveRows(all: AdminOutputSection[], scope: number | null): Row[] {
  if (scope === null) {
    return all.filter((s) => s.retailerId === null).sort(byOrder).map((section) => ({ section, inherited: false, hiddenBy: null }));
  }
  const own = all.filter((s) => s.retailerId === scope);
  const universal = all.filter((s) => s.retailerId === null && s.isActive);
  const rows: Row[] = [];
  for (const u of universal) {
    const mine = own.find((o) => o.key === u.key);
    if (!mine) rows.push({ section: u, inherited: true, hiddenBy: null });
    else if (mine.isHidden) rows.push({ section: u, inherited: true, hiddenBy: mine });
    // else: replaced — the retailer copy is listed with the own rows below
  }
  for (const o of own) if (!o.isHidden) rows.push({ section: o, inherited: false, hiddenBy: null });
  return rows.sort((a, b) => byOrder(a.section, b.section));
}

export const AdminOutputSections: React.FC = () => {
  const [sections, setSections] = useState<AdminOutputSection[]>([]);
  const [retailers, setRetailers] = useState<OutputSectionRetailerSummary[]>([]);
  const [scope, setScope] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [translations, setTranslations] = useState<TranslationValues>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const res = await fetchOutputSections();
    setSections(res.sections);
    setRetailers(res.retailers);
  }, []);

  useEffect(() => {
    reload().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load')).finally(() => setLoading(false));
  }, [reload]);

  const rows = useMemo(() => effectiveRows(sections, scope), [sections, scope]);
  const retailerName = (id: number | null) => (id === null ? 'Universal' : retailers.find((r) => r.id === id)?.name ?? `#${id}`);

  const run = async (fn: () => Promise<void>, okMsg?: string) => {
    setSaving(true); setError(null); setNotice(null);
    try { await fn(); await reload(); if (okMsg) setNotice(okMsg); }
    catch (e) { setError(e instanceof Error ? e.message : 'Request failed'); }
    finally { setSaving(false); }
  };

  // ── list actions ──────────────────────────────────────────────────────────
  const openEditor = (s: AdminOutputSection) => {
    setDraft({ id: s.id, retailerId: s.retailerId, key: s.key, title: s.title, content: clone(s.content), isActive: s.isActive });
    setTranslations({});
    setNotice(null); setError(null);
  };

  const customize = (u: AdminOutputSection) => run(async () => {
    const res = await createOutputSection({ retailerId: scope, key: u.key, title: u.title, content: clone(u.content), isActive: true });
    openEditor(res.section);
  }, `Copied "${u.title}" for ${retailerName(scope)} — edit it below.`);

  const hide = (u: AdminOutputSection) => run(
    () => createOutputSection({ retailerId: scope, key: u.key, title: u.title, isHidden: true }).then(() => undefined),
    `"${u.title}" is now hidden for ${retailerName(scope)}.`,
  );

  const revert = (s: AdminOutputSection, label: string) => {
    if (!window.confirm(`${label} "${s.title}"?`)) return;
    run(() => deleteOutputSection(s.id), 'Done.').then(() => { if (draft?.id === s.id) setDraft(null); });
  };

  const toggleActive = (s: AdminOutputSection) => run(() => updateOutputSection(s.id, { isActive: !s.isActive }).then(() => undefined));

  /**
   * Move an owned row within the effective list. Universal rows are spaced by
   * 10; a retailer's own rows take numbers between the inherited neighbours so
   * the merge on the results page lands them where the admin put them.
   */
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    const order: { id: number; sortOrder: number }[] = [];
    if (scope === null) {
      next.forEach((r, i) => order.push({ id: r.section.id, sortOrder: i * 10 }));
    } else {
      let prev = -10; let k = 0;
      next.forEach((r, i) => {
        if (r.inherited) { prev = r.section.sortOrder; k = 0; return; }
        k += 1;
        const nextInherited = next.slice(i + 1).find((x) => x.inherited);
        const so = nextInherited ? Math.min(prev + k, nextInherited.section.sortOrder) : prev + k * 10;
        order.push({ id: r.section.id, sortOrder: so });
      });
    }
    if (order.length) run(() => reorderOutputSections(scope, order));
  };

  // ── editor ────────────────────────────────────────────────────────────────
  const setContent = (patch: Partial<OutputSectionContent>) => setDraft((d) => (d ? { ...d, content: { ...d.content, ...patch } } : d));
  const setDisplay = (patch: Partial<OutputSectionContent['display']>) => setDraft((d) => (d ? { ...d, content: { ...d.content, display: { ...d.content.display, ...patch } } } : d));
  const setPage = (patch: Partial<OutputSectionContent['page']>) => setDraft((d) => (d ? { ...d, content: { ...d.content, page: { ...d.content.page, ...patch } } } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const payload: OutputSectionPayload = {
        retailerId: draft.retailerId, key: draft.key.trim(), title: draft.title.trim(),
        content: draft.content, isActive: draft.isActive,
      };
      const res = draft.id ? await updateOutputSection(draft.id, payload) : await createOutputSection(payload);
      // Translations save AFTER the row (a new section has no id until then).
      // A failure here is reported but never rolls back the English.
      let msg = 'Section saved.';
      try { await saveTranslations('output_sections', res.section.id, translations); }
      catch { msg = 'Section saved, but the translations could not be stored. Open it again and retry.'; }
      await reload();
      setDraft(null);
      setNotice(msg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const previewHtml = useMemo(
    () => (draft ? DOMPurify.sanitize(draft.content.html, { ADD_ATTR: ['target'] }) : ''),
    [draft],
  );

  const translatableFields = draft ? [
    { path: 'title', label: 'Title', english: draft.title },
    { path: 'content_json.html', label: 'Content', english: draft.content.html, richText: true, imageUpload: { kind: 'section-image' as const, retailerId: draft.retailerId ?? 0 } },
  ] : [];

  if (loading) return <div className="p-4">Loading output sections…</div>;

  return (
    <div className="bg-white rounded-3 shadow-sm p-4 admin-output-sections">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2"><FileText size={22} /> Output Sections</h2>
          <p className="text-muted mb-0">
            Rich-text sections shown after the estimate tables — on screen, in print and in the PDF.
            The <strong>Universal</strong> set applies to every retailer; a retailer can customise or hide any of them.
          </p>
        </div>
        {!draft && (
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => { setDraft(blankDraft(scope)); setTranslations({}); }} disabled={saving}>
            <Plus size={15} /> New section
          </button>
        )}
      </div>

      {/* Scope picker */}
      {!draft && (
        <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
          <div>
            <label className="form-label small fw-semibold mb-1">Showing</label>
            <select className="form-select" style={{ minWidth: 280 }} value={scope === null ? 'universal' : String(scope)}
              onChange={(e) => { setScope(e.target.value === 'universal' ? null : Number(e.target.value)); setNotice(null); setError(null); }} disabled={saving}>
              <option value="universal">Universal (default set)</option>
              {retailers.map((r) => {
                const n = sections.filter((s) => s.retailerId === r.id).length;
                return <option key={r.id} value={r.id}>{r.name}{n ? ` — ${n} custom` : ''}</option>;
              })}
            </select>
          </div>
          {scope !== null && <span className="text-muted small mb-2">This is what {retailerName(scope)} estimates will show, in order.</span>}
        </div>
      )}

      {error && <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}
      {notice && <div className="alert alert-success py-2 px-3 small">{notice}</div>}

      {/* List */}
      {!draft && (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr><th style={{ width: 90 }}>Order</th><th>Title</th><th>Source</th><th>Screen</th><th>Print</th><th>PDF</th><th className="text-end">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="text-muted">No sections yet.</td></tr>}
              {rows.map((r, idx) => {
                const s = r.section;
                const hidden = !!r.hiddenBy;
                const ownedHere = !r.inherited;
                return (
                  <tr key={`${s.id}-${r.hiddenBy?.id ?? ''}`} className={!s.isActive || hidden ? 'text-muted' : ''}>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" title="Move up" disabled={saving || !ownedHere || idx === 0} onClick={() => move(idx, -1)}><ArrowUp size={12} /></button>
                        <button className="btn btn-outline-secondary" title="Move down" disabled={saving || !ownedHere || idx === rows.length - 1} onClick={() => move(idx, 1)}><ArrowDown size={12} /></button>
                      </div>
                    </td>
                    <td>
                      <span className={hidden ? 'text-decoration-line-through' : ''}>{s.title || <em>untitled</em>}</span>
                      <div className="small text-muted">{s.key}{!s.isActive && ' · inactive'}</div>
                    </td>
                    <td>
                      {r.inherited
                        ? <span className={`badge ${hidden ? 'text-bg-secondary' : 'text-bg-light border'}`}>{hidden ? 'Hidden here' : 'Universal'}</span>
                        : scope === null
                          ? <span className="badge text-bg-light border">Universal</span>
                          : <span className="badge text-bg-primary">{sections.some((u) => u.retailerId === null && u.key === s.key) ? 'Customized' : 'Retailer only'}</span>}
                    </td>
                    <td className="small">{SCREEN_MODES.find((m) => m.value === s.content.display.screen)?.label ?? s.content.display.screen}</td>
                    <td className="small">{s.content.display.print ? 'Yes' : 'No'}</td>
                    <td className="small">{s.content.display.pdf ? 'Yes' : 'No'}</td>
                    <td className="text-end text-nowrap">
                      {r.inherited && !hidden && (
                        <>
                          <button className="btn btn-sm btn-outline-primary me-1" disabled={saving} onClick={() => customize(s)} title="Make a copy this retailer can edit"><Copy size={13} className="me-1" />Customize</button>
                          <button className="btn btn-sm btn-outline-secondary" disabled={saving} onClick={() => hide(s)} title="Do not show this section for this retailer"><EyeOff size={13} className="me-1" />Hide</button>
                        </>
                      )}
                      {r.inherited && hidden && (
                        <button className="btn btn-sm btn-outline-secondary" disabled={saving} onClick={() => revert(r.hiddenBy!, 'Show again')}><Eye size={13} className="me-1" />Unhide</button>
                      )}
                      {ownedHere && (
                        <>
                          <button className="btn btn-sm btn-outline-secondary me-1" disabled={saving} onClick={() => openEditor(s)}>Edit</button>
                          {scope === null && (
                            <button className="btn btn-sm btn-outline-secondary me-1" disabled={saving} onClick={() => toggleActive(s)}>{s.isActive ? 'Deactivate' : 'Activate'}</button>
                          )}
                          {scope !== null && sections.some((u) => u.retailerId === null && u.key === s.key) ? (
                            <button className="btn btn-sm btn-outline-danger" disabled={saving} onClick={() => revert(s, 'Revert to the universal version of')} title="Delete this copy and use the universal section"><RotateCcw size={13} className="me-1" />Revert</button>
                          ) : (
                            <button className="btn btn-sm btn-outline-danger" disabled={saving} onClick={() => revert(s, 'Delete')} title="Delete"><Trash2 size={13} /></button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {scope === null && (
            <p className="text-muted small mb-0">Deactivated universal sections are hidden for everyone; deleting one also deletes its translations and any retailer copies keep working on their own.</p>
          )}
        </div>
      )}

      {/* Editor */}
      {draft && (
        <div className="row g-4">
          <div className="col-lg-7">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{draft.id ? 'Edit section' : 'New section'} <span className="text-muted fw-normal small">— {retailerName(draft.retailerId)}</span></h5>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setDraft(null)} disabled={saving}><X size={15} className="me-1" /> Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  {saving ? <Loader2 size={15} className="me-1" style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} className="me-1" />} Save
                </button>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-sm-8">
                <label className="form-label small fw-semibold mb-1">Title</label>
                <input className="form-control" value={draft.title} maxLength={255} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </div>
              <div className="col-sm-4">
                <label className="form-label small fw-semibold mb-1">Key</label>
                <input className="form-control" value={draft.key} disabled={!!draft.id} pattern="[a-zA-Z][a-zA-Z0-9_-]{0,63}"
                  onChange={(e) => setDraft({ ...draft, key: e.target.value })} title="Letters, digits, - and _; fixed once saved" />
                <div className="form-text">{draft.id ? 'Fixed once saved.' : 'A retailer copy with the same key replaces the universal section.'}</div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-sm-4">
                <label className="form-label small fw-semibold mb-1">On screen</label>
                <select className="form-select" value={draft.content.display.screen} onChange={(e) => setDisplay({ screen: e.target.value as SectionScreenMode })}>
                  {SCREEN_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="col-sm-8 d-flex flex-wrap align-items-end gap-4 pb-2">
                <div className="form-check form-switch m-0">
                  <input type="checkbox" className="form-check-input" role="switch" id="os-print" checked={draft.content.display.print} onChange={(e) => setDisplay({ print: e.target.checked })} />
                  <label className="form-check-label" htmlFor="os-print">Print</label>
                </div>
                <div className="form-check form-switch m-0">
                  <input type="checkbox" className="form-check-input" role="switch" id="os-pdf" checked={draft.content.display.pdf} onChange={(e) => setDisplay({ pdf: e.target.checked })} />
                  <label className="form-check-label" htmlFor="os-pdf">PDF</label>
                </div>
                <div className="form-check form-switch m-0">
                  <input type="checkbox" className="form-check-input" role="switch" id="os-active" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
                  <label className="form-check-label" htmlFor="os-active">Active</label>
                </div>
              </div>
            </div>

            <fieldset className="border rounded-3 p-3 mb-3">
              <legend className="float-none w-auto px-2 small fw-semibold mb-0">Page controls (print &amp; PDF)</legend>
              <div className="row g-3 align-items-end">
                <div className="col-sm-4">
                  <div className="form-check form-switch m-0">
                    <input type="checkbox" className="form-check-input" role="switch" id="os-newpage" checked={draft.content.page.startOnNewPage} onChange={(e) => setPage({ startOnNewPage: e.target.checked })} />
                    <label className="form-check-label" htmlFor="os-newpage">Start on a new page</label>
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="form-check form-switch m-0">
                    <input type="checkbox" className="form-check-input" role="switch" id="os-breakafter" checked={draft.content.page.breakAfter} onChange={(e) => setPage({ breakAfter: e.target.checked })} />
                    <label className="form-check-label" htmlFor="os-breakafter">Nothing else on its last page</label>
                  </div>
                </div>
                <div className="col-sm-4">
                  <label className="form-label small fw-semibold mb-1">Max pages</label>
                  <input type="number" className="form-control" min={1} max={SECTION_MAX_PAGES} placeholder="No limit"
                    value={draft.content.page.maxPages ?? ''}
                    onChange={(e) => { const n = parseInt(e.target.value, 10); setPage({ maxPages: Number.isFinite(n) && n > 0 ? Math.min(SECTION_MAX_PAGES, n) : null }); }} />
                </div>
              </div>
              <div className="form-text">Max pages scales the whole section down (text, images and tables together, to no less than half size) until everything fits within that many pages — nothing is cut off. The PDF fits exactly; print fits approximately (Letter pages).</div>
            </fieldset>

            <label className="form-label small fw-semibold mb-1">Content</label>
            <Suspense fallback={<div className="text-muted small py-3"><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading editor…</div>}>
              <RichTextEditor
                key={`${draft.retailerId ?? 'u'}-${draft.id ?? 'new'}`}
                semantic
                height={420}
                imageUpload={{ kind: 'section-image', retailerId: draft.retailerId ?? 0 }}
                value={draft.content.html}
                onChange={(html) => setContent({ html })}
              />
            </Suspense>
            <p className="text-muted small mt-2 mb-3">
              Headings, paragraphs, lists, links, tables and images only — colours and sizes come from the site theme and retailer branding.
              Images placed one after another sit side by side (up to 4 per row in the PDF); put text or an empty paragraph between them to start a new row.
              Empty table cells print as boxes to write in. Use the <code>&lt;/&gt;</code> button to edit the HTML directly.
            </p>

            <TranslationFields
              entityType="output_sections"
              entityId={draft.id}
              fields={translatableFields}
              values={translations}
              onChange={setTranslations}
              title="This section in other languages"
            />
          </div>

          {/* Live preview */}
          <div className="col-lg-5">
            <div className="card">
              <div className="card-header d-flex align-items-center gap-2 py-2"><Eye size={15} /> <span className="fw-semibold small">Preview (Trusscore theme)</span></div>
              <div className="card-body">
                <section className="output-section os">
                  <div className="os__heading"><h2 className="section-title os__title"><FileText size={22} className="section-title-icon" /> {draft.title || 'Untitled'}</h2></div>
                  <div className="os__body"><div className="os__html" dangerouslySetInnerHTML={{ __html: previewHtml }} /></div>
                </section>
                <p className="text-muted small mb-0 mt-2">Styling on a retailer portal follows that retailer's colours. Save to see the server-cleaned HTML.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOutputSections;
